import { useCallback, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BannerContext } from '../contexts/BannerContext';
import useAPI from './useApi';
import {
  notifyTradeJournalChanged,
  refreshTradeJournalsCache,
  removeTradeJournalFromCache,
  upsertTradeJournalInCache,
} from '../utils/tradeJournalCache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useTradeJournals = () => {
  const { request } = useAPI();
  const queryClient = useQueryClient();
  const { showMessage } = useContext(BannerContext);

  const createTradeJournal = useCallback(async (entry, account_id = null, trade_id = null) => {
    try {
      const url = `${BACKEND_URL}/trade_journals`;
      const payload = { entry, account_id, ...(trade_id ? { trade_id } : {}) };
      console.log('[useTradeJournals] Creating journal with payload:', { account_id, trade_id, hasEntry: !!entry });
      const res = await request(url, {}, 'POST', payload);
      if (!res || res.error) {
        showMessage('Error saving journal', 'danger');
      } else {
        showMessage('Journal saved', 'success');
        upsertTradeJournalInCache(queryClient, res);
        await refreshTradeJournalsCache(queryClient);
        notifyTradeJournalChanged('created');
      }
      return res;
    } catch (e) {
      showMessage('Error saving journal', 'danger');
      return null;
    }
  }, [request, showMessage, queryClient]);

  const getTradeJournals = useCallback(() => {
    const url = `${BACKEND_URL}/trade_journals`
    return request(url, {}, 'GET');
  }, [request]);

  const getTradeJournalByTradeId = useCallback((tradeId) => {
    if (!tradeId) return Promise.resolve(null);
    const url = `${BACKEND_URL}/trade_journals?trade_id=${encodeURIComponent(tradeId)}`;
    return request(url, {}, 'GET').then((rows) => {
      if (!Array.isArray(rows) || rows.length === 0) return null;
      return rows[0] || null;
    });
  }, [request]);

  const deleteTradeJournal = useCallback(async (id) => {
    const url = `${BACKEND_URL}/trade_journals/` + id;
    const res = await request(url, {}, 'DELETE');
    if (res && !res.error) {
      removeTradeJournalFromCache(queryClient, id);
      await refreshTradeJournalsCache(queryClient);
      notifyTradeJournalChanged('updated');
    }
    return res;
  }, [request, queryClient]);

  const updateTradeJournal = useCallback(async (id, entry, account_id = null) => {
    try {
      const url = `${BACKEND_URL}/trade_journals/` + id;
      const payload = { entry };
      if (account_id) {
        payload.account_id = account_id;
      }
      const res = await request(url, {}, 'PUT', payload);
      if (!res || res.error) {
        showMessage('Error updating journal', 'danger');
      } else {
        showMessage('Journal updated', 'success');
        upsertTradeJournalInCache(queryClient, res?.id ? res : { id, entry, account_id });
        await refreshTradeJournalsCache(queryClient);
        notifyTradeJournalChanged('updated');
      }
      return res;
    } catch (e) {
      showMessage('Error updating journal', 'danger');
      return null;
    }
  }, [request, showMessage, queryClient]);

  const getTradeJournalTemplate = useCallback(() => {
    const url = `${BACKEND_URL}/trade_journal_template`;
    return request(url, {}, 'GET');
  }, [request]);

  const saveTradeJournalTemplate = useCallback((template, name = 'Default') => {
    const url = `${BACKEND_URL}/trade_journal_template`;
    return request(url, {}, 'PUT', { name, template });
  }, [request]);

  return {
    createTradeJournal,
    getTradeJournals,
    getTradeJournalByTradeId,
    deleteTradeJournal,
    updateTradeJournal,
    getTradeJournalTemplate,
    saveTradeJournalTemplate,
  };
}